import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/common/components/atoms/Button/Button';
import {
  ORDER_ACTIONS,
  ORDER_STATUS,
  SHIPPING_TYPES,
  TYPE_ORIGIN,
} from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useConfig } from '@/common/contexts/ConfigContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import DeliveryNotes from '@/ebat/components/blocks/Modal/Order/DeliveryNotes/DeliveryNotes';
import ShippingRequest from '@/ebat/components/blocks/Modal/Order/ShippingRequest/ShippingRequest';

import {
  ListActionDefaultProps,
  ListActionPropTypes,
} from './ListAction.proptypes';

const ListAction = ({
  delivered,
  deliveryNotes,
  disabled,
  id,
  isLoading,
  isShippingAvailable,
  origin,
  reference,
  shippingType,
  status,
}) => {
  const { t } = useTranslation();
  const { user, zergRush } = useAuth();
  const { isChrono, isEbat } = useConfig();
  const {
    onOpenModal,
    setModalClassName,
    setModalContent,
    setModalSize,
  } = useModalContext();

  /**
   * Check status for request action
   * @type {boolean}
   */
  const isOrderStatusRequest = useMemo(
    () =>
      [
        ORDER_STATUS.IN_PROCCESS,
        ORDER_STATUS.PARTIALLY_PICKED_UP_OR_INVOICED,
        ORDER_STATUS.PARTIALLY_SHIPPED_OR_INVOICED,
        ORDER_STATUS.PROCESS_SEE_DETAILS,
        ORDER_STATUS.SAVED,
      ].includes(status.code),
    [status],
  );

  /**
   * Check status for delivery note action
   * @type {boolean}
   */
  const isOrderStatusDeliveryNotes = useMemo(
    () =>
      [
        ORDER_STATUS.INVOICED,
        ORDER_STATUS.PARTIALLY_PICKED_UP_OR_INVOICED,
        ORDER_STATUS.PARTIALLY_SHIPPED_OR_INVOICED,
        ORDER_STATUS.PICKED_UP,
        ORDER_STATUS.SHIPPED,
      ].includes(status.code),
    [status],
  );

  /**
   * Check if order has shipping request
   * @type {boolean}
   */
  const hasShippingRequest = useMemo(
    () => isOrderStatusRequest && isShippingAvailable,
    [isOrderStatusRequest, isShippingAvailable],
  );

  /**
   * Check if order has provision request
   * @type {boolean}
   */
  const hasProvisionRequest = useMemo(
    () =>
      !delivered &&
      isOrderStatusRequest &&
      isShippingAvailable &&
      [SHIPPING_TYPES.shipping_drive, SHIPPING_TYPES.shipping_collect].includes(
        shippingType.id,
      ),
    [delivered, isOrderStatusRequest, isShippingAvailable, shippingType],
  );

  /**
   * Check if order has delivery notes
   * @type {boolean}
   */
  const hasDeliveryNotes = useMemo(
    () =>
      deliveryNotes.length > 0 &&
      !hasShippingRequest &&
      !hasProvisionRequest &&
      isOrderStatusDeliveryNotes &&
      isShippingAvailable,
    [
      deliveryNotes,
      hasProvisionRequest,
      hasShippingRequest,
      isOrderStatusDeliveryNotes,
      isShippingAvailable,
    ],
  );

  const isDisabled = useMemo(
    () => ({
      loading: isLoading,
      disabled:
        isLoading ||
        disabled ||
        zergRush ||
        (origin === TYPE_ORIGIN.EB && isChrono) ||
        (origin === TYPE_ORIGIN.EBC && isEbat) ||
        !user?.functionality_accesses?.orders_request_availability ||
        !user?.functionality_accesses?.orders_request_delivery,
    }),
    [disabled, isChrono, isEbat, isLoading, origin, user, zergRush],
  );

  /**
   * Return label of action button
   * @type {string}
   */
  const getLabel = useMemo(() => {
    if (hasProvisionRequest) {
      return ORDER_ACTIONS.PROVISION_REQUEST;
    }

    if (hasShippingRequest) {
      return ORDER_ACTIONS.SHIPPING_REQUEST;
    }

    if (hasDeliveryNotes) {
      return ORDER_ACTIONS.DELIVERY_NOTES;
    }

    return null;
  }, [hasDeliveryNotes, hasProvisionRequest, hasShippingRequest]);

  /**
   * Opening of a modal according to type
   * @type {function(*): null}
   */
  const handleOpenModal = useCallback(() => {
    switch (getLabel) {
      case ORDER_ACTIONS.DELIVERY_NOTES:
        setModalContent(
          <DeliveryNotes deliveryNotes={deliveryNotes} orderId={id} />,
        );
        setModalClassName('modal-delivery-notes');
        setModalSize('sm');
        break;
      case ORDER_ACTIONS.SHIPPING_REQUEST:
      case ORDER_ACTIONS.PROVISION_REQUEST:
        setModalContent(
          <ShippingRequest
            orderId={id}
            reference={reference}
            refetchOrder={() => {}}
            type={
              hasProvisionRequest
                ? ORDER_ACTIONS.PROVISION_REQUEST
                : ORDER_ACTIONS.SHIPPING_REQUEST
            }
          />,
        );
        setModalClassName('modal-shipping-request');
        setModalSize('sm');
        break;
      default:
        return null;
    }

    return onOpenModal();
  }, [
    deliveryNotes,
    getLabel,
    hasProvisionRequest,
    id,
    onOpenModal,
    reference,
    setModalClassName,
    setModalContent,
    setModalSize,
  ]);

  if (getLabel) {
    return (
      <Button
        buttonSize="small"
        className="button--action"
        disabled={isDisabled.disabled}
        onClick={handleOpenModal}
        variant="small-primary"
      >
        {t(`ebat:entities:orders.button.${getLabel}`)}
      </Button>
    );
  }

  return null;
};

ListAction.defaultProps = {
  ...ListActionDefaultProps,
};

ListAction.propTypes = {
  ...ListActionPropTypes,
};

export default ListAction;
